// Reading the `text/html` a spreadsheet puts on the clipboard alongside its
// plain-text TSV, to recover FORMATTING on an external paste. `clipboard.ts`'s
// header is right that TSV is the whole VALUE interop story — but Excel and
// Google Sheets both also emit an HTML `<table>` of the same range, and that
// table carries fill colour, bold/italic, font colour, alignment and borders
// that the TSV throws away.
//
// What this can NEVER carry: a FORMULA. No clipboard format a browser exposes
// to a web page holds one — Excel's formula-bearing formats (BIFF, "XML
// Spreadsheet 2003") are invisible outside native apps. A pasted `=SUM(...)`
// arrives as the number it last evaluated to, in every spreadsheet's clipboard
// HTML, not only here. Formula fidelity only exists via an uploaded .xlsx —
// see `xlsxImport.ts`.
//
// `DOMParser` would make the table-walking trivial, and like `xml.ts` we don't
// use it: it does not exist in Node, so a unit test could not run the same
// code the app runs, and it chokes on real-world clipboard HTML (unquoted
// attributes, bare `<br>`, a stray unclosed tag) in ways that would rather
// lose one cell's formatting than throw away the whole paste.

import type { Border, BorderEdge, CellStyle, FormatId, Merge } from '@/model/types';
import { inferLiteral } from '@/model/workbook';
import { decodeXml } from '@/data/xml';

export interface HtmlPaste {
  values: string[][];
  formats: (FormatId | undefined)[][];
  styles: (CellStyle | undefined)[][];
  /** Merges, 0-based offsets from the table's own top-left — same convention as `ClipboardPayload.merges`. */
  merges: Merge[];
}

/**
 * Parse a clipboard `text/html` payload into a grid, or `null` if it has no
 * table to read (callers fall back to the plain-text TSV path).
 *
 * A `rowspan`/`colspan` cell becomes a real merge (see `merges`) AND keeps the
 * existing value/style-on-top-left, every-other-slot-blank layout below —
 * gTables needs the grid aligned either way, merge or no merge.
 */
export function parseHtmlTable(html: string): HtmlPaste | null {
  const fragment = extractFragment(html);
  const tableMatch = /<table\b[\s\S]*?<\/table>/i.exec(fragment);
  const tableHtml = tableMatch ? tableMatch[0] : fragment;
  const rowMatches = [...tableHtml.matchAll(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi)];
  if (rowMatches.length === 0) return null;

  const rules = parseStylesheet(html);
  const values: string[][] = [];
  const formats: (FormatId | undefined)[][] = [];
  const styles: (CellStyle | undefined)[][] = [];
  const merges: Merge[] = [];
  // Columns a previous row's `rowspan` still reserves: col -> rows left after this one.
  const occupied = new Map<number, number>();

  for (const rowMatch of rowMatches) {
    const cellMatches = [...rowMatch[1]!.matchAll(/<t([dh])\b([^>]*)>([\s\S]*?)<\/t[dh]>/gi)];
    const rowValues: string[] = [];
    const rowFormats: (FormatId | undefined)[] = [];
    const rowStyles: (CellStyle | undefined)[] = [];
    let col = 0;

    const consumeReserved = (): void => {
      while (occupied.has(col)) {
        rowValues[col] = '';
        const left = occupied.get(col)!;
        if (left <= 1) occupied.delete(col);
        else occupied.set(col, left - 1);
        col++;
      }
    };

    for (const cellMatch of cellMatches) {
      consumeReserved();
      const tag = cellMatch[1]!.toLowerCase();
      const attrs = parseAttrString(cellMatch[2]!);
      const classes = (attrs.class ?? '').split(/\s+/).filter(Boolean);
      const decls = { ...stylesheetDeclsFor(rules, tag, classes), ...parseDeclarations(attrs.style ?? '') };
      const text = cellText(cellMatch[3]!);
      const colspan = Math.max(1, parseInt(attrs.colspan ?? '1', 10) || 1);
      const rowspan = Math.max(1, parseInt(attrs.rowspan ?? '1', 10) || 1);
      if (colspan > 1 || rowspan > 1) {
        merges.push({ r1: values.length, c1: col, r2: values.length + rowspan - 1, c2: col + colspan - 1 });
      }

      rowValues[col] = text;
      rowFormats[col] = inferLiteral(text).fmt;
      rowStyles[col] = styleFromDecls(decls, attrs);
      if (rowspan > 1) occupied.set(col, (occupied.get(col) ?? 0) + rowspan - 1);
      col++;
      for (let k = 1; k < colspan; k++) {
        consumeReserved();
        rowValues[col] = '';
        if (rowspan > 1) occupied.set(col, (occupied.get(col) ?? 0) + rowspan - 1);
        col++;
      }
    }
    consumeReserved();
    values.push(rowValues);
    formats.push(rowFormats);
    styles.push(rowStyles);
  }
  return { values, formats, styles, merges };
}

/** Excel/Chrome/Sheets all wrap the copied range in these HTML comments. */
function extractFragment(html: string): string {
  const start = html.indexOf('<!--StartFragment-->');
  const end = html.indexOf('<!--EndFragment-->');
  if (start >= 0 && end > start) return html.slice(start + '<!--StartFragment-->'.length, end);
  return html;
}

function cellText(inner: string): string {
  const withBreaks = inner.replace(/<br\s*\/?>/gi, '\n');
  const stripped = withBreaks.replace(/<[^>]+>/g, '');
  const decoded = decodeXml(stripped.replace(/&nbsp;/gi, ' '));
  return decoded
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .trim();
}

function parseAttrString(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([\w:-]+)\s*=\s*("([^"]*)"|'([^']*)'|([^\s"'>]+))/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    out[m[1]!.toLowerCase()] = m[3] ?? m[4] ?? m[5] ?? '';
  }
  return out;
}

function parseDeclarations(css: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const part of css.split(';')) {
    const colon = part.indexOf(':');
    if (colon < 0) continue;
    const prop = part.slice(0, colon).trim().toLowerCase();
    const value = part.slice(colon + 1).trim();
    if (prop && value) out[prop] = value;
  }
  return out;
}

interface StyleRule {
  selectors: string[];
  decls: Record<string, string>;
}

/**
 * Windows Excel's clipboard HTML styles cells by CLASS, referencing a
 * `<style>` block of rules like `.xl65 { font-weight:700; ... }` rather than
 * inline `style=` — unlike Google Sheets, which is all inline. Both are
 * handled: inline declarations are merged in afterwards and win.
 */
function parseStylesheet(html: string): StyleRule[] {
  const rules: StyleRule[] = [];
  const blocks = html.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) ?? [];
  for (const block of blocks) {
    const inner = block.replace(/<style[^>]*>/i, '').replace(/<\/style>/i, '');
    const ruleRe = /([^{}]+)\{([^{}]*)\}/g;
    let m: RegExpExecArray | null;
    while ((m = ruleRe.exec(inner))) {
      const selectors = m[1]!.split(',').map((s) => s.trim()).filter(Boolean);
      const decls = parseDeclarations(m[2]!);
      if (selectors.length > 0 && Object.keys(decls).length > 0) rules.push({ selectors, decls });
    }
  }
  return rules;
}

/** Only the selector's last token matters — good enough for `.xl65` or `td`, not a real cascade. */
function matchSelector(selector: string, tag: string, classes: string[]): boolean {
  const token = selector.split(/\s+/).pop() ?? '';
  if (token.startsWith('.')) return classes.includes(token.slice(1));
  return token.toLowerCase() === tag;
}

function stylesheetDeclsFor(rules: StyleRule[], tag: string, classes: string[]): Record<string, string> {
  let out: Record<string, string> = {};
  for (const rule of rules) {
    if (rule.selectors.some((s) => matchSelector(s, tag, classes))) out = { ...out, ...rule.decls };
  }
  return out;
}

function styleFromDecls(decls: Record<string, string>, attrs: Record<string, string>): CellStyle | undefined {
  const style: CellStyle = {};

  const bg = colorToken(decls['background-color'] ?? decls['background']) ?? colorToken(attrs.bgcolor);
  if (bg) style.bg = bg;

  const color = colorToken(decls.color);
  if (color) style.color = color;

  const weight = decls['font-weight'];
  if (weight === 'bold' || weight === 'bolder' || (Number(weight) >= 600 && !Number.isNaN(Number(weight)))) {
    style.bold = true;
  }

  const fontStyle = decls['font-style'];
  if (fontStyle === 'italic' || fontStyle === 'oblique') style.italic = true;

  const align = decls['text-align'] ?? attrs.align;
  if (align === 'left' || align === 'center' || align === 'right') style.align = align;

  const whiteSpace = decls['white-space'];
  if (whiteSpace === 'nowrap') style.overflow = 'clip';
  else if (whiteSpace === 'normal' || whiteSpace === 'pre-wrap' || whiteSpace === 'pre-line') style.overflow = 'wrap';

  const borders = bordersFromDecls(decls);
  if (borders) style.borders = borders;

  return Object.keys(style).length > 0 ? style : undefined;
}

const BORDER_EDGES: BorderEdge[] = ['top', 'right', 'bottom', 'left'];

function bordersFromDecls(decls: Record<string, string>): Partial<Record<BorderEdge, Border>> | undefined {
  const shorthand = decls.border !== undefined ? parseBorderValue(decls.border) : undefined;
  const out: Partial<Record<BorderEdge, Border>> = {};
  for (const edge of BORDER_EDGES) {
    const specific = decls[`border-${edge}`];
    const border = specific !== undefined ? parseBorderValue(specific) : shorthand;
    if (border) out[edge] = border;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

const BORDER_STYLE_WORDS = /^(solid|dashed|dotted|double|groove|ridge|inset|outset)$/i;

function parseBorderValue(raw: string): Border | undefined {
  const v = raw.trim();
  if (!v || /^(none|hidden)$/i.test(v)) return undefined;
  const widthMatch = /([\d.]+)\s*(px|pt)?/.exec(v);
  let w = widthMatch ? parseFloat(widthMatch[1]!) : 1;
  if (widthMatch?.[2] === 'pt') w = Math.max(1, Math.round((w * 96) / 72));
  if (!Number.isFinite(w) || w <= 0) w = 1;

  const rgb = /rgba?\([^)]*\)/i.exec(v)?.[0];
  const hex = /#[0-9a-fA-F]{3,8}/.exec(v)?.[0];
  const word = v
    .split(/\s+/)
    .find((tok) => !/^[\d.]+(px|pt|em)?$/.test(tok) && !BORDER_STYLE_WORDS.test(tok) && !/^(rgba?\(.*\)?)$/i.test(tok));
  return { w, c: rgb ?? hex ?? word ?? '#000000' };
}

function colorToken(v: string | undefined): string | undefined {
  if (!v) return undefined;
  const t = v.trim();
  if (!t || /^(transparent|inherit|initial|unset|none)$/i.test(t)) return undefined;
  const m = /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]*\)|[a-zA-Z]+)/.exec(t);
  return m ? m[0] : t;
}
